'use client';

import { Input, Textarea } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';

import { UsernameInput } from '@/core/components/auth/username-input';
import { LogoUpload } from '@/core/components/settings/logo-upload';
import { useDictionary } from '@/core/providers/dictionary-provider';

interface WorkspaceDetailsFormProps {
  name: string;
  onNameChange: (name: string) => void;
  username?: string;
  onUsernameChange?: (username: string) => void;
  description?: string;
  onDescriptionChange?: (description: string) => void;
  photoPreview?: string;
  onPhotoChange: (
    fileId: string | undefined,
    preview: string | undefined,
  ) => void;
  nameError?: string | null;
  usernameError?: string | null;
}

export function WorkspaceDetailsForm({
  name,
  onNameChange,
  username = '',
  onUsernameChange,
  description = '',
  onDescriptionChange,
  photoPreview,
  onPhotoChange,
  nameError,
  usernameError,
}: WorkspaceDetailsFormProps) {
  const dict = useDictionary();
  const handleNameChange = (value: string) => {
    onNameChange(value);
  };

  const handleLogoUpload = (fileId: string, preview: string) => {
    onPhotoChange(fileId, preview);
  };

  const handleLogoRemove = () => {
    onPhotoChange(undefined, undefined);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="workspace-name">
          {dict.workspace.create.workspaceDetails.nameLabel}
          <span className="ml-0.5 text-destructive">*</span>
        </Label>
        <Input
          id="workspace-name"
          type="text"
          placeholder={dict.workspace.create.workspaceDetails.namePlaceholder}
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          className={nameError ? 'border-destructive' : ''}
        />
        {nameError && <p className="text-sm text-destructive">{nameError}</p>}
      </div>

      {onUsernameChange && (
        <div className="space-y-2">
          <Label htmlFor="workspace-username">
            {dict.workspace.create.workspaceDetails.usernameLabel ||
              'URL short name'}
            <span className="ml-0.5 text-destructive">*</span>
          </Label>
          <UsernameInput
            id="workspace-username"
            value={username}
            onChange={onUsernameChange}
            workspaceName={name}
            error={usernameError || undefined}
          />
          {dict.workspace.create.workspaceDetails.usernameSectionHint ? (
            <p className="text-xs leading-snug text-muted-foreground">
              {dict.workspace.create.workspaceDetails.usernameSectionHint}
            </p>
          ) : null}
          {dict.workspace.create.workspaceDetails.usernameHelperText ? (
            <p className="text-xs text-muted-foreground">
              {dict.workspace.create.workspaceDetails.usernameHelperText.replace(
                '{username}',
                username.trim() || '…',
              )}
            </p>
          ) : null}
        </div>
      )}

      {onDescriptionChange && (
        <div className="space-y-2">
          <Label htmlFor="workspace-description">
            {dict.workspace.create.workspaceDetails.descriptionLabel}
          </Label>
          <Textarea
            id="workspace-description"
            className="text-sm"
            placeholder={
              dict.workspace.create.workspaceDetails.descriptionPlaceholder
            }
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={3}
          />
        </div>
      )}

      <LogoUpload
        label={dict.workspace.create.workspaceDetails.photoLabel}
        description={dict.workspace.create.workspaceDetails.photoDescription}
        currentPreview={photoPreview}
        onUpload={handleLogoUpload}
        onRemove={handleLogoRemove}
      />
    </div>
  );
}
